import type {
    HistoryDomainAdapterRegistry,
    HistoryDomainAdapter,
    HistoryPatchApplyInput,
} from './domain';
import type {
    CadTransaction,
    EntityPatch,
    HistoryApplyDirection,
} from './types';

/**
 * 补丁应用上下文。
 */
export interface PatchApplierContext<TContext = unknown> {
    /** 历史领域适配器注册表。 */
    readonly adapterRegistry: HistoryDomainAdapterRegistry;
    /** 透传给领域 adapter 的上下文。 */
    readonly domainContext: TContext;
}

/**
 * 已解析补丁应用项。
 */
interface ResolvedPatchApplication<TContext = unknown> {
    /** 补丁。 */
    readonly patch: EntityPatch;
    /** 负责补丁的 adapter。 */
    readonly adapter: HistoryDomainAdapter;
    /** 正向应用输入。 */
    readonly input: HistoryPatchApplyInput<unknown, TContext>;
    /** 失败回滚输入。 */
    readonly rollbackInput: HistoryPatchApplyInput<unknown, TContext>;
}

/**
 * 创建补丁应用输入。
 *
 * @param transaction 当前事务。
 * @param patch 当前补丁。
 * @param direction 应用方向。
 * @param context 调用方上下文。
 * @returns 补丁应用输入。
 */
function createPatchApplyInput<TContext>(
    transaction: CadTransaction,
    patch: EntityPatch,
    direction: HistoryApplyDirection,
    context: TContext,
): HistoryPatchApplyInput<unknown, TContext> {
    return {
        direction,
        patch,
        snapshot: direction === 'undo' ? patch.before : patch.after,
        context,
    };
}

/**
 * 解析并预校验补丁应用项。
 *
 * @param transaction 当前事务。
 * @param direction 应用方向。
 * @param context 补丁应用上下文。
 * @returns 已解析补丁应用项。
 */
function resolvePatchApplications<TContext>(
    transaction: CadTransaction,
    direction: HistoryApplyDirection,
    context: PatchApplierContext<TContext>,
): readonly ResolvedPatchApplication<TContext>[] {
    return transaction.changeSet.patches.map((patch) => {
        const adapter = context.adapterRegistry.assert(patch.adapterId);

        if (adapter.kind !== patch.entityKind) {
            throw new Error(`补丁 ${patch.entityId} 的实体类型与 adapter ${adapter.adapterId} 不一致。`);
        }

        return {
            patch,
            adapter,
            input: createPatchApplyInput(
                transaction,
                patch,
                direction,
                context.domainContext,
            ),
            rollbackInput: createPatchApplyInput(
                transaction,
                patch,
                direction === 'undo' ? 'redo' : 'undo',
                context.domainContext,
            ),
        };
    });
}

/**
 * 回滚已经应用的补丁。
 *
 * @param appliedApplications 已应用补丁。
 * @param cause 原始错误。
 */
function rollbackAppliedPatches<TContext>(
    appliedApplications: readonly ResolvedPatchApplication<TContext>[],
    cause: unknown,
): void {
    try {
        [...appliedApplications].reverse().forEach((application) => {
            application.adapter.applySnapshot(application.rollbackInput);
        });
    } catch (rollbackError) {
        throw new Error('补丁应用失败，且回滚已应用补丁失败。', {
            cause: rollbackError ?? cause,
        });
    }

    throw cause;
}

/**
 * 应用事务补丁。
 *
 * 设计说明：
 * 1. Phase 1 只通过 `HistoryDomainAdapter` 抽象恢复快照；
 * 2. 此处禁止 import Sketch、Feature、Assembly、Three.js 或 Worker 具体实现；
 * 3. 如果 adapter 抛错，调用方必须保持 history cursor 不前进。
 *
 * @param transaction 当前事务。
 * @param direction 应用方向。
 * @param context 补丁应用上下文。
 */
export function applyTransactionPatches<TContext>(
    transaction: CadTransaction,
    direction: HistoryApplyDirection,
    context: PatchApplierContext<TContext>,
): void {
    const applications = resolvePatchApplications(transaction, direction, context);
    const appliedApplications: ResolvedPatchApplication<TContext>[] = [];

    try {
        applications.forEach((application) => {
            application.adapter.applySnapshot(application.input);
            appliedApplications.push(application);
        });
    } catch (error) {
        rollbackAppliedPatches(appliedApplications, error);
    }
}
